import React from 'react';
import {FormProvider, useFieldArray, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import noop from 'lodash/noop';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalContent,
  ModalHeader,
  showModal,
} from '@edna/components';
import {ECurrencySymbol, TCurrency} from '@edna/utils';

import {ModalLayout} from 'src/components/primitives';
import {EWAConversation} from 'src/containers/SummaryReport/definitions';

import {TActiveTariff, TTariffTemplate, TWhatsappTariffPayload} from '../definitions';
import {detailsToCountryPrices} from '../utils';
import {WhatsAppPersonalTariffContent} from './TariffContent';
import {TFormData} from './definitions';
import * as S from './style';

export const TariffCostModal = React.memo<{
  closeModal: TEmptyFunction;
  currencyCode: TCurrency;
  licenseCurrencyCode: TCurrency;
  dialogCurrencyCode: TCurrency;
  tariff: TActiveTariff<TWhatsappTariffPayload> | TTariffTemplate<TWhatsappTariffPayload>;
}>(({currencyCode, licenseCurrencyCode, dialogCurrencyCode, closeModal, tariff}) => {
  const {t} = useTranslation();
  const dialogCurrency = ECurrencySymbol[dialogCurrencyCode];

  const initialValues = React.useMemo(
    () => ({
      mauCount: tariff.payload.mauCount ?? 0,
      initPayment: tariff.payload.initPayment ?? 0,
      licensePrice: tariff.payload.licensePrice ?? 0,
      defaultMauPrice: tariff.payload.defaultMauPrice ?? 0,
      conversationCountryScaleList: detailsToCountryPrices(tariff.payload.details),
    }),
    [tariff],
  );

  const methods = useForm<TFormData>({
    defaultValues: initialValues,
  });

  const {fields: conversationCountryScaleList} = useFieldArray<
    TFormData,
    'conversationCountryScaleList'
  >({
    name: 'conversationCountryScaleList',
    control: methods.control,
  });

  return (
    <Modal width="840px">
      <ModalHeader>{t('Tariffs:tariffCostModal.title')}</ModalHeader>
      <ModalLayout>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(noop)}>
            <ModalContent>
              <WhatsAppPersonalTariffContent
                currencyCode={currencyCode}
                licenseCurrencyCode={licenseCurrencyCode}
                dialogCurrencyCode={dialogCurrencyCode}
                readOnly
                licensePeriodMonths={tariff.payload.licensePeriodMonths}
              />
              {!!conversationCountryScaleList.length && (
                <S.CollapsibleSection
                  isOpenDefault
                  label={t('Tariffs:tariffCostModal.dialogsCost', {
                    currency: dialogCurrency,
                  })}
                >
                  <S.Tr>
                    <S.Th>{t('Tariffs:createPersonalTariff.labels.country')}</S.Th>
                    <S.Th>{t(`ChannelReport:whatsappReport.${EWAConversation.SERVICE}`)}</S.Th>
                    <S.Th>{t(`ChannelReport:whatsappReport.${EWAConversation.UTILITY}`)}</S.Th>
                    <S.Th>
                      {t(`ChannelReport:whatsappReport.${EWAConversation.AUTHENTICATION}`)}
                    </S.Th>
                    <S.Th>{t(`ChannelReport:whatsappReport.${EWAConversation.MARKETING}`)}</S.Th>
                  </S.Tr>
                  <S.ScrolledContent maxHeightPercent={40}>
                    {conversationCountryScaleList.map((countryScale, index) => (
                      <S.Tr key={countryScale.id}>
                        <S.Td>
                          {countryScale.country.toLowerCase() === 'undefined'
                            ? t('Country:other')
                            : t(`Country:iso3.${countryScale.country}`)}
                        </S.Td>
                        {[
                          EWAConversation.SERVICE,
                          EWAConversation.UTILITY,
                          EWAConversation.AUTHENTICATION,
                          EWAConversation.MARKETING,
                        ].map((conversationType) => {
                          const priceIndex = countryScale?.prices?.findIndex(
                            (price) => price.conversationType === conversationType,
                          );

                          return (
                            <S.Td key={conversationType}>
                              <S.NewNumberInputField
                                name={`conversationCountryScaleList.${index}.prices.${priceIndex}.price`}
                                isDecimal
                                readOnly
                              />
                            </S.Td>
                          );
                        })}
                      </S.Tr>
                    ))}
                  </S.ScrolledContent>
                </S.CollapsibleSection>
              )}

              <ModalActions>
                <ModalCancelButton data-selector="ModalCancelButton" onClick={closeModal}>
                  {t('Common:button.close')}
                </ModalCancelButton>
              </ModalActions>
            </ModalContent>
          </form>
        </FormProvider>
      </ModalLayout>
    </Modal>
  );
});

export const showTariffCostModal = (
  currencyCode: TCurrency,
  licenseCurrencyCode: TCurrency,
  dialogCurrencyCode: TCurrency,
  tariff: TActiveTariff<TWhatsappTariffPayload> | TTariffTemplate<TWhatsappTariffPayload>,
) =>
  showModal((closeModal) => (
    <TariffCostModal
      currencyCode={currencyCode}
      licenseCurrencyCode={licenseCurrencyCode}
      dialogCurrencyCode={dialogCurrencyCode}
      closeModal={closeModal}
      tariff={tariff}
    />
  ));
